/**
 * Filtros del catálogo de ejercicios: el mismo objeto viaja en los parámetros
 * de búsqueda de `/exercises` y en el estado local del selector de ejercicios
 * del editor de rutinas.
 */
import { z } from 'zod';

import type { ExerciseListParams } from '@/lib/queries/exercises';
import { CATEGORY_KEYS } from '@/lib/i18n/training';

/**
 * `catch(undefined)` en cada campo: una URL manipulada a mano no debe tumbar la
 * pantalla, simplemente pierde ese filtro.
 */
export const exerciseFiltersSchema = z.object({
  q: z.string().trim().max(80).optional().catch(undefined),
  muscle: z.string().max(60).optional().catch(undefined),
  equipment: z.string().max(60).optional().catch(undefined),
  category: z.enum(CATEGORY_KEYS).optional().catch(undefined),
  ownerOnly: z.boolean().optional().catch(undefined),
});

export type ExerciseFilters = z.infer<typeof exerciseFiltersSchema>;

export const EMPTY_FILTERS: ExerciseFilters = {};

/** Cuántos filtros hay puestos, contando la búsqueda por texto. */
export function countActiveFilters(filters: ExerciseFilters): number {
  let count = 0;
  if (filters.q !== undefined && filters.q !== '') count += 1;
  if (filters.muscle !== undefined) count += 1;
  if (filters.equipment !== undefined) count += 1;
  if (filters.category !== undefined) count += 1;
  if (filters.ownerOnly === true) count += 1;
  return count;
}

/** Traduce los filtros a los parámetros que espera `GET /v1/exercises`. */
export function toListParams(filters: ExerciseFilters): ExerciseListParams {
  const params: ExerciseListParams = {};
  if (filters.q !== undefined && filters.q !== '') params.q = filters.q;
  if (filters.muscle !== undefined) params.muscle = filters.muscle;
  if (filters.equipment !== undefined) params.equipment = filters.equipment;
  if (filters.category !== undefined) params.category = filters.category;
  if (filters.ownerOnly === true) params.ownerOnly = true;
  return params;
}

/**
 * Cambia un filtro. Volver a pulsar el valor que ya estaba puesto lo quita, que
 * es lo que espera cualquiera al tocar un chip encendido.
 */
export function toggleFilter<K extends keyof ExerciseFilters>(
  filters: ExerciseFilters,
  key: K,
  value: ExerciseFilters[K],
): ExerciseFilters {
  const next = { ...filters };
  if (next[key] === value) delete next[key];
  else next[key] = value;
  return next;
}
